import { apiClient } from '@/shared/api';
import type {
  KmAttrType,
  KmAttrTypeOptions,
  KmAttrTypesParams,
  KmEntityType,
  KmEntityTypeAttr,
  KmEntityTypeCreate,
  KmEntityTypesParams,
  KmEntityTypeUpdate,
  KmPage,
  KmRelationType,
  KmRelationTypesParams,
} from '../types';

/*
 * Этот модуль — граница entity: бэкенд km отдаёт и принимает поля в snake_case, а
 * внутрь приложения уходят только доменные типы в camelCase (см. `../types`). Здесь
 * описаны сырые ответы бэкенда, мапперы ответов (snake_case → camelCase) и приведение
 * имён query-параметров к контракту бэкенда. Сырые типы наружу не экспортируются.
 */

// --- Сырые ответы бэкенда (snake_case) ---

type RawKmPage<T> = {
  total: number;
  items: T[];
};

type RawKmEntityType = {
  code: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_deleted: boolean;
  attr_count: number | null;
  created_at: string;
  updated_at: string;
};

type RawKmEntityTypeAttr = {
  entity_type_code: string;
  attr_type_code: string;
  is_required: boolean;
  display_order: number;
  is_deleted: boolean;
};

type RawKmAttrTypeOptions = {
  choices?: { code: string; label: string }[];
  ref_entity_type?: string;
  fields?: { attr_code: string; is_required: boolean; display_order: number }[];
};

type RawKmAttrType = {
  code: string;
  name: string;
  description: string | null;
  data_type: string;
  is_list: boolean;
  options: RawKmAttrTypeOptions | null;
};

type RawKmRelationType = {
  code: string;
  name: string;
  description: string | null;
  is_directed: boolean;
  from_entity_type_code: string;
  to_entity_type_code: string;
  is_deleted: boolean;
};

// --- Мапперы ответов в доменные типы (snake_case → camelCase) ---

const mapPage = <TRaw, T>(page: RawKmPage<TRaw>, mapItem: (raw: TRaw) => T): KmPage<T> => ({
  total: page.total,
  items: page.items.map(mapItem),
});

const mapEntityType = (raw: RawKmEntityType): KmEntityType => ({
  code: raw.code,
  name: raw.name,
  description: raw.description,
  isSystem: raw.is_system,
  isDeleted: raw.is_deleted,
  attrCount: raw.attr_count,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
});

const mapEntityTypeAttr = (raw: RawKmEntityTypeAttr): KmEntityTypeAttr => ({
  entityTypeCode: raw.entity_type_code,
  attrTypeCode: raw.attr_type_code,
  isRequired: raw.is_required,
  displayOrder: raw.display_order,
  isDeleted: raw.is_deleted,
});

const mapAttrTypeOptions = (raw: RawKmAttrTypeOptions | null): KmAttrTypeOptions | null =>
  raw
    ? {
        choices: raw.choices,
        refEntityType: raw.ref_entity_type,
        fields: raw.fields?.map((field) => ({
          attrCode: field.attr_code,
          isRequired: field.is_required,
          displayOrder: field.display_order,
        })),
      }
    : null;

const mapAttrType = (raw: RawKmAttrType): KmAttrType => ({
  code: raw.code,
  name: raw.name,
  description: raw.description,
  dataType: raw.data_type,
  isList: raw.is_list,
  options: mapAttrTypeOptions(raw.options),
});

const mapRelationType = (raw: RawKmRelationType): KmRelationType => ({
  code: raw.code,
  name: raw.name,
  description: raw.description,
  isDirected: raw.is_directed,
  fromEntityTypeCode: raw.from_entity_type_code,
  toEntityTypeCode: raw.to_entity_type_code,
  isDeleted: raw.is_deleted,
});

// --- Приведение query-параметров к контракту бэкенда (camelCase → snake_case) ---
// Пагинация km задана бэкендом единым форматом limit/offset; include_deleted — snake_case.
// apiClient оставляет эти имена как есть (kebab-преобразование их не затрагивает).

const entityTypesParamsToApi = (params?: KmEntityTypesParams) => ({
  include_deleted: params?.includeDeleted,
  limit: params?.limit,
  offset: params?.offset,
});

const attrTypesParamsToApi = (params?: KmAttrTypesParams) => ({
  include_deleted: params?.includeDeleted,
  limit: params?.limit,
  offset: params?.offset,
});

const relationTypesParamsToApi = (params?: KmRelationTypesParams) => ({
  from: params?.from,
  to: params?.to,
  include_deleted: params?.includeDeleted,
  limit: params?.limit,
  offset: params?.offset,
});

/**
 * GET /km/entity-types — страница типов сущностей.
 */
export const fetchKmEntityTypes = async (params?: KmEntityTypesParams): Promise<KmPage<KmEntityType>> => {
  const { data } = await apiClient.get<RawKmPage<RawKmEntityType>>('/km/entity-types', {
    params: entityTypesParamsToApi(params),
  });
  return mapPage(data, mapEntityType);
};

/**
 * GET /km/entity-types/{code} — карточка типа сущности.
 */
export const fetchKmEntityType = async (code: string): Promise<KmEntityType> => {
  const { data } = await apiClient.get<RawKmEntityType>(`/km/entity-types/${code}`);
  return mapEntityType(data);
};

/**
 * POST /km/entity-types — создание типа сущности.
 */
export const createKmEntityType = async (payload: KmEntityTypeCreate): Promise<KmEntityType> => {
  const { data } = await apiClient.post<RawKmEntityType>('/km/entity-types', payload);
  return mapEntityType(data);
};

/**
 * PATCH /km/entity-types/{code} — изменение типа сущности.
 */
export const updateKmEntityType = async ({
  code,
  payload,
}: {
  code: string;
  payload: KmEntityTypeUpdate;
}): Promise<KmEntityType> => {
  const { data } = await apiClient.patch<RawKmEntityType>(`/km/entity-types/${code}`, payload);
  return mapEntityType(data);
};

/**
 * DELETE /km/entity-types/{code} — мягкое удаление типа сущности.
 */
export const deleteKmEntityType = async (code: string): Promise<void> => {
  await apiClient.delete(`/km/entity-types/${code}`);
};

/**
 * GET /km/entity-types/{code}/attrs — схема типа (привязки атрибутов).
 */
export const fetchKmEntityTypeAttrs = async (code: string): Promise<KmPage<KmEntityTypeAttr>> => {
  const { data } = await apiClient.get<RawKmPage<RawKmEntityTypeAttr>>(`/km/entity-types/${code}/attrs`, {
    params: { limit: 1000 },
  });
  return mapPage(data, mapEntityTypeAttr);
};

/**
 * GET /km/attr-types — справочник типов атрибутов.
 */
export const fetchKmAttrTypes = async (params?: KmAttrTypesParams): Promise<KmPage<KmAttrType>> => {
  const { data } = await apiClient.get<RawKmPage<RawKmAttrType>>('/km/attr-types', {
    params: attrTypesParamsToApi(params),
  });
  return mapPage(data, mapAttrType);
};

/**
 * GET /km/relation-types — типы связей (фильтры from/to).
 */
export const fetchKmRelationTypes = async (params?: KmRelationTypesParams): Promise<KmPage<KmRelationType>> => {
  const { data } = await apiClient.get<RawKmPage<RawKmRelationType>>('/km/relation-types', {
    params: relationTypesParamsToApi(params),
  });
  return mapPage(data, mapRelationType);
};
